import * as Yup from 'yup';
import { forwardRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import { Dialog, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
// routes
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { useRouter } from 'src/routes/hooks';
// config
import { PATH_AFTER_LOGIN } from 'src/config-global';
// hooks
import { useBoolean } from 'src/hooks/use-boolean';
// auth
import useApiContext from 'src/hooks/use-api';
import { useDispatch } from 'src/store';
import { signin } from 'src/store/reducers/auth';
// components
import Iconify from 'src/components/iconify';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { LogoDark } from 'src/components/logo';

// ----------------------------------------------------------------------

// eslint-disable-next-line prefer-arrow-callback
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function JwtLoginView({
  status,
  onCloseModal,
}: {
  status: boolean;
  onCloseModal: () => void;
}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { login } = useApiContext();

  const [errorMsg, setErrorMsg] = useState('');

  const password = useBoolean();

  const LoginSchema = Yup.object().shape({
    email: Yup.string().required('Email is required').email('Email must be a valid email address'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const response = await login({ email: data.email, password: data.password });
      if (response?.data?.user && response?.data?.token) {
        dispatch(signin(response.data));
        onCloseModal();
        router.push(PATH_AFTER_LOGIN);
      }
    } catch (error) {
      console.error(error);
      reset();
      setErrorMsg(typeof error === 'string' ? error : error.message);
    }
  });

  const renderHead = (
    <Stack spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 5 }}>
      <LogoDark />

      {/* <Typography variant="h4">Sign in</Typography>

      <Stack direction="row" spacing={0.5}>
        <Typography variant="body2">{`Don't have an account yet?`}</Typography>

        <Link component={RouterLink} href={paths.auth.register} variant="subtitle2">
          Create an account
        </Link>
      </Stack> */}
    </Stack>
  );

  const renderForm = (
    <Stack spacing={2.5}>
      {!!errorMsg && <Alert severity="error">{errorMsg}</Alert>}

      <RHFTextField name="email" label="Email address" />

      <RHFTextField
        name="password"
        label="Password"
        type={password.value ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify
                  icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                  color="#ddd"
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* <Link variant="body2" color="inherit" underline="always" sx={{ alignSelf: 'flex-end' }}>
        Forgot password?
      </Link> */}

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        sx={{
          background: '#ff3c00',
          color: '#fff',
          '&:hover': { background: '#ff3c00' },
        }}
      >
        Login
      </LoadingButton>
    </Stack>
  );

  return (
    <Dialog
      open={status}
      TransitionComponent={Transition}
      maxWidth="xs"
      fullWidth
      onClose={onCloseModal}
      sx={{
        '& .MuiModal-backdrop': {
          backdropFilter: 'blur(8px)',
        },
        '& .MuiDialog-paper': {
          width: { xs: '90%' },
          backgroundColor: '#f6f7f8',
          py: 3,
          px: 2,
        },
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        {renderHead}

        {renderForm}
      </FormProvider>
    </Dialog>
  );
}
